import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/Checkbox'
import { ExistingTestChat } from '@/redux/slices/testChatSlice'
import { useAppSelector } from '@/redux/hooks'
import { RootState } from '@/redux/store'
import { Clock, MessageSquare } from 'lucide-react'
import { formatDate } from '@/utils/dateTimeUtils'
import { cn } from '@/lib/utils'

interface PreviousTestChatsModalProps {
  isOpen: boolean
  onClose: () => void
  onViewChat: (chatId: number) => void
  onViewMultipleChats: (chatIds: number[]) => void
}

const PreviousTestChatsModal: React.FC<PreviousTestChatsModalProps> = ({
  isOpen,
  onClose,
  onViewChat,
  onViewMultipleChats,
}) => {
  const { existingTestChats, openChatBoxes } = useAppSelector(
    (state: RootState) => state.testChatMessages
  )
  const [selectedChats, setSelectedChats] = useState<Set<number>>(new Set())

  useEffect(() => {
    if (isOpen) {
      const currentlyOpenChatIds = new Set(
        openChatBoxes
          .filter((box) => !box.isTestChat && box.chatData !== null)
          .map((box) => box.id)
      )
      setSelectedChats(currentlyOpenChatIds)
    } else {
      setSelectedChats(new Set())
    }
  }, [isOpen, openChatBoxes])

  const handleToggleChat = (chatId: number) => {
    const newSelected = new Set(selectedChats)
    if (newSelected.has(chatId)) {
      newSelected.delete(chatId)
    } else {
      if (newSelected.size + openChatBoxes.length >= 3) {
        return
      }
      newSelected.add(chatId)
    }
    setSelectedChats(newSelected)
  }

  const handleViewSelected = () => {
    if (selectedChats.size === 0) return

    if (selectedChats.size === 1) {
      const chatId = Array.from(selectedChats)[0]
      onViewChat(chatId)
    } else {
      const chatIds = Array.from(selectedChats)
      onViewMultipleChats(chatIds)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='sm:max-w-[700px] max-h-[80vh]'>
        <DialogHeader>
          <DialogTitle>Previous Test Chats</DialogTitle>
          <DialogDescription>
            Select up to 3 test chats to view simultaneously. Previous chats are
            read-only.
          </DialogDescription>
        </DialogHeader>
        <div className='overflow-y-auto max-h-[60vh]'>
          {existingTestChats.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <MessageSquare className='w-16 h-16 text-gray-400 mb-4' />
              <p className='text-gray-500 text-lg font-medium'>
                No previous test chats found
              </p>
              <p className='text-gray-400 text-sm mt-2'>
                Start a test chat to create one
              </p>
            </div>
          ) : (
            <div className='space-y-2'>
              <div className='flex items-center justify-end p-4 bg-gray-50 rounded-lg border'>
                <span className='text-sm font-medium text-gray-700'>
                  Selected ({selectedChats.size + openChatBoxes.length}/3 max)
                </span>
              </div>
              {existingTestChats.map(
                (chat: ExistingTestChat, index: number) => {
                  const isSelected = selectedChats.has(chat.id)
                  const canSelect =
                    selectedChats.size + openChatBoxes.length < 3 || isSelected

                  return (
                    <div
                      key={chat.id}
                      className={cn(
                        'flex items-start gap-3 p-4 border rounded-lg transition-colors cursor-pointer',
                        canSelect
                          ? 'hover:bg-gray-50'
                          : 'opacity-50 cursor-not-allowed'
                      )}
                      onClick={() => canSelect && handleToggleChat(chat.id)}
                    >
                      <Checkbox
                        checked={isSelected}
                        onChange={() => canSelect && handleToggleChat(chat.id)}
                        className='mt-1 w-4 h-4'
                        disabled={!canSelect}
                      />
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-start justify-between gap-2'>
                          <div className='flex-1'>
                            <p className='font-medium text-gray-900'>
                              {`Test Chat #${index + 1}`}
                            </p>
                            <div className='flex items-center gap-4 mt-2 text-sm text-gray-600'>
                              <div className='flex items-center gap-1'>
                                <MessageSquare className='w-4 h-4' />
                                <span>{chat.message_count} messages</span>
                              </div>
                              <div className='flex items-center gap-1'>
                                <Clock className='w-4 h-4' />
                                <span>{formatDate(chat.created_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className='mt-2 flex items-center gap-2 text-xs text-gray-500'>
                          <span className='px-2 py-1 bg-blue-100 text-blue-700 rounded'>
                            {chat.llm_model}
                          </span>
                          <span className='px-2 py-1 bg-gray-100 text-gray-700 rounded'>
                            Temp: {chat.temperature}
                          </span>
                          <span className='px-2 py-1 bg-gray-100 text-gray-700 rounded'>
                            Tokens: {chat.max_tokens}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                }
              )}
            </div>
          )}
        </div>
        <DialogFooter className='flex gap-2 sm:justify-end'>
          <Button variant='outline' onClick={onClose}>
            Close
          </Button>
          <Button
            className='bg-dark-blue hover:bg-dark-blue-hovered'
            onClick={handleViewSelected}
            disabled={selectedChats.size === 0}
          >
            {selectedChats.size === 0
              ? 'View Selected Chat'
              : selectedChats.size === 1
                ? 'View Selected Chat'
                : `View ${selectedChats.size} Chats`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default PreviousTestChatsModal
